import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useState } from "react";
import LazyImage from "./ui/LazyImage";
import ImageSlider from "./ImageSlider";
import { createPortal } from "react-dom";
import GalleryMedia from "./GalleryMedia";
import { AspectRatio } from "./ui/aspect-ratio";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import Logo from "../assets/images/green-logo.png";
import { Pagination, Navigation } from "swiper/modules";

type ImageItem = {
  id?: number | string;
  url: string;
  caption?: string | null;
};

type MediaForm = "form1" | "form2";

type MediaViewerProps = {
  images: ImageItem[];
  form: MediaForm;
  activeIndex: number;
  onChange: (index: number) => void;
  forceSlider?: boolean;
  enableMobileViewer?: boolean;
};

export default function MediaViewer({
  images,
  form,
  activeIndex,
  onChange,
  forceSlider = false,
  enableMobileViewer = false,
}: MediaViewerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(activeIndex || 0);
  const [, setViewerStartIndex] = useState(0);
  const openViewer = (index: number) => {
    setViewerIndex(index);
    setViewerStartIndex(index);
    setIsOpen(true);
  };
  const closeViewer = () => {
    setIsOpen(false);
  };

  if (!images.length) {
    return (
      <div className="relative w-full h-48 border border-green-04/15 rounded-2xl select-none overflow-hidden">
        <div className="h-full rounded-[20px]">
          <AspectRatio ratio={16 / 9}>
            <LazyImage
              src={Logo}
              alt="logo"
              className="w-full h-full object-contain"
              draggable={false}
            />
          </AspectRatio>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="relative w-full h-full overflow-hidden rounded-2xl">
        {form === "form2" && (
          <div className="absolute inset-0 z-0">
            <ImageSlider
              images={images}
              activeIndex={activeIndex}
              onChange={onChange}
              isBackground
              isFullHeight
              alwaysSwiper={forceSlider}
              onImageClick={enableMobileViewer ? openViewer : undefined}
            />
          </div>
        )}

        {form === "form1" && (
          <ImageSlider
            images={images}
            activeIndex={activeIndex}
            onChange={onChange}
            alwaysSwiper={forceSlider}
            onImageClick={enableMobileViewer ? openViewer : undefined}
          />
        )}
      </div>

      {isOpen &&
        images[viewerIndex] &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
            onClick={closeViewer}
          >
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                closeViewer();
              }}
              className="absolute top-4 right-4 pt-1 size-10 rounded-full bg-white/10 text-white text-2xl flex items-center justify-center z-20"
            >
              ×
            </button>

            <div
              className="w-full h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <Swiper
                modules={[Navigation, Pagination]}
                navigation={images.length > 1}
                slidesPerView={1}
                spaceBetween={0}
                initialSlide={viewerIndex}
                onSlideChange={(swiper: SwiperType) => {
                  setViewerIndex(swiper.activeIndex);
                }}
                pagination={
                  images.length > 1
                    ? {
                        clickable: true,
                        dynamicBullets: true,
                        dynamicMainBullets: 3,
                      }
                    : false
                }
                className="w-full h-full [&_.swiper-pagination]:!bottom-6"
              >
                {images.map((img, i) => (
                  <SwiperSlide key={img.id ?? i}>
                    <div
                      className="w-full h-full flex items-center justify-center px-2"
                      onClick={(e) => {
                        if (e.target === e.currentTarget) closeViewer();
                      }}
                    >
                      <GalleryMedia
                        active={viewerIndex === i}
                        src={img.url}
                        alt={img.caption ?? "تصویر"}
                        className="max-w-full max-h-[90vh] object-contain rounded-2xl select-none"
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
              <style>{`
                      .swiper-button-next,
                      .swiper-button-prev {
                        color: white;
                        transform: scale(0.7);
                        width: 55px;
                        height: 55px;
                        padding: 18px;
                      }

                      .swiper-button-next:after,
                      .swiper-button-prev:after {
                        font-size: 18px;
                      }
                    `}</style>
            </div>

            {images.length > 1 && (
              <div className="absolute bottom-14 left-1/2 -translate-x-1/2 text-white text-xs bg-white/10 px-3 py-1 rounded-full z-20">
                {viewerIndex + 1} / {images.length}
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
